import * as Sentry from '@sentry/react';
import { API as AmplifyAPI, Auth } from 'aws-amplify';

import { AppViewMode, UnAuthAPIName } from 'src/constants';
import { primaryDomainHostname } from 'src/constants/hostnameConsts';
import { buildApiSpanName } from 'src/utils/apiSpanName';
import { UseUnAuthApp } from 'src/utils/AuthUtils';
import { bucket } from 'src/utils/bucket';
import { handleNativeHttpRequestFetcher } from 'src/utils/httpUtils';
import { withPosthogSession } from 'src/utils/posthogSession';
import { readIsProxySession } from 'src/utils/proxySession';
import {
  getSentrySampleRatio,
  traceIdFromTraceparent,
  TRACEPARENT_HEADER,
  withTraceparent,
} from 'src/utils/traceparent';
import {
  isWorkspaceDeletedError,
  leaveDeletedWorkspace,
} from 'src/utils/workspaceDeleted';

// general type for init, copied from aws-amplify
type initType = {
  [key: string]: any;
};

const defaultApiBaseUrl = `https://app-api.${primaryDomainHostname.prod}/`;

const normalizeRelativeApiPath = (path: string) => path.replace(/^\/+/, '');

const ensureTrailingSlash = (value: string) =>
  value.endsWith('/') ? value : `${value}/`;

// op for the Sentry span covering an entire API request (see attachSentrySpan
// below), distinct from the child spans Dynamo etc. add on the backend side.
const API_SPAN_OP = 'http.client';

export const getConfiguredApiBaseUrl = (apiName: string) => {
  if (typeof window === 'undefined' || !window.App?.portalConfig) {
    return defaultApiBaseUrl;
  }

  const configuredEndpoint = window.App.portalConfig.AWS?.API?.endpoints?.find(
    (endpoint: { name: string; endpoint: string }) => endpoint.name === apiName,
  )?.endpoint;

  if (configuredEndpoint) {
    return configuredEndpoint;
  }

  if (window.App.portalConfig.BaseAPIUrl) {
    return window.App.portalConfig.BaseAPIUrl;
  }

  return defaultApiBaseUrl;
};

export const buildApiEndpointUrl = (apiName: string, path: string) =>
  new URL(
    normalizeRelativeApiPath(path),
    ensureTrailingSlash(getConfiguredApiBaseUrl(apiName)),
  ).toString();

/**
 * Setup init object for api call based on api name.
 * For api names that correspond to unauth endpoints, return existing init object.
 * For other api names (all auth) add auth header from current user token.
 * @param apiName name of api being called
 * @param init init object passed to api call
 * @returns modified init object with auth header if needed
 */
export const getInitForApi = async (
  apiName: string,
  init: initType,
): Promise<initType> => {
  const isUnauthRoute = UseUnAuthApp(window.location.pathname);
  // when it is client view mode, the portal header
  // information lives in the portal config and we dont need
  // to get it from the session info since we dont have the notion
  // of multiple sessions in client experience.

  // when it is internal view mode, the portal header information lives in
  // two places depending on the route.

  // 1. if the route is an un-auth route,we dont know which portal is being accessed
  // so use the portal header from the portal config which defines the unauth portal
  // header (workspace).

  // 2. if the route is an auth route, use the portal header from the session info
  // which defines the actual portal the user is accessing.
  const Portal =
    isUnauthRoute || window.App.portalConfig.viewMode === AppViewMode.CLIENT
      ? window.App.portalConfig.portalHeader
      : window.App.sessionData.currentPortalSession;

  if (apiName === UnAuthAPIName) {
    return {
      ...init,
      headers: withPosthogSession(
        withTraceparent({
          ...(init.headers ?? {}),
          Portal,
        }),
      ),
    };
  }

  // get current session from amplify auth (which does refresh)
  const currentSession = await Auth.currentSession();
  const idToken = currentSession.getIdToken().getJwtToken();
  const Authorization = `Bearer ${idToken}`;
  const Company = localStorage.getItem('activeCompanyId');
  // Mirror the /admin proxy-session marker set at OnboardingAuth signin to the backend.
  const isProxySession = readIsProxySession();
  const headers: Record<string, unknown> = {
    // Default the company scope to the active company, but let a caller
    // override it per-request (e.g. fetching another company's installs).
    Company,
    ...(init.headers ?? {}),
    Authorization,
    Portal,
  };
  if (isProxySession) {
    headers['X-Proxy-Session'] = 'true';
  }
  return {
    ...init,
    headers: withPosthogSession(withTraceparent(headers)),
  };
};

// Rebuilds a traceparent header with a different parent-id (span-id) segment,
// keeping the same version/trace-id/flags.
const withParentId = (traceparent: string, parentId: string) => {
  const [version, traceId, , flags] = traceparent.split('-');
  return `${version}-${traceId}-${parentId}-${flags}`;
};

/**
 * For the subset of browser trace ids selected by sentrySampleRatio, starts a
 * standalone Sentry span for this request and rewrites requestInit's
 * traceparent so its parent-id segment is this span's id — the backend's
 * server span then becomes a child of THIS span, not of the page load span.
 *
 * This lives here (API.get/post/put/patch/del), not in withTraceparent or
 * getInitForApi, because both of those are also called by app-builder
 * streaming code, which must never get a Sentry span.
 */
const attachSentrySpan = (
  method: string,
  path: string,
  requestInit: initType,
): Sentry.Span | undefined => {
  const traceparent = requestInit.headers?.[TRACEPARENT_HEADER] as
    | string
    | undefined;
  if (!traceparent) {
    return undefined;
  }

  const traceId = traceIdFromTraceparent(traceparent);
  if (!bucket(traceId, getSentrySampleRatio())) {
    return undefined;
  }

  let span: Sentry.Span | undefined;
  Sentry.withScope((scope) => {
    // sampleRand is required for the sampling decision to stick — without
    // it the span is silently dropped even though sampled: true is set.
    scope.setPropagationContext({
      traceId,
      sampled: true,
      sampleRand: Math.random(),
    });
    span = Sentry.startInactiveSpan({
      name: buildApiSpanName(method, path),
      op: API_SPAN_OP,
      forceTransaction: true,
      // Explicitly no parent: without this the span silently inherits the
      // ambient pageload/navigation span instead of rooting its own trace.
      parentSpan: null,
      attributes: { 'http.request.method': method.toUpperCase() },
    });
  });

  if (!span) {
    return undefined;
  }

  requestInit.headers = {
    ...requestInit.headers,
    [TRACEPARENT_HEADER]: withParentId(traceparent, span.spanContext().spanId),
  };

  return span;
};

const runWithSentrySpan = async <T>(
  method: string,
  path: string,
  requestInit: initType,
  send: () => Promise<T>,
): Promise<T> => {
  const span = attachSentrySpan(method, path, requestInit);
  if (!span) {
    return send();
  }

  try {
    const result = await send();
    span.setStatus({ code: 1 }); // SPAN_STATUS_OK
    return result;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (typeof status === 'number') {
      Sentry.setHttpStatus(span, status);
    } else {
      span.setStatus({ code: 2 }); // SPAN_STATUS_ERROR — no HTTP status available
    }
    throw error;
  } finally {
    span.end();
  }
};

/**
 * This class is a wrapper around Amplify's API class.
 * It allows us to add additional headers to the request (e.g. Authorization).
 * based on a refreshed token.
 * It can also be used to mock Amplify's API class in tests.
 */
async function callApi<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    if (isWorkspaceDeletedError(error)) {
      leaveDeletedWorkspace();
    }
    throw error;
  }
}

export class API {
  static async get(
    apiName: string,
    path: string,
    init: initType,
  ): Promise<any> {
    const requestInit = await getInitForApi(apiName, init);
    return callApi(
      runWithSentrySpan('GET', path, requestInit, () =>
        process.env.JEST_WORKER_ID
          ? handleNativeHttpRequestFetcher(
              buildApiEndpointUrl(apiName, path),
              'GET',
              undefined,
              undefined,
            )
          : AmplifyAPI.get(apiName, path, requestInit),
      ),
    );
  }

  static async post(
    apiName: string,
    path: string,
    init: initType,
  ): Promise<any> {
    const requestInit = await getInitForApi(apiName, init);
    return callApi(
      runWithSentrySpan('POST', path, requestInit, () =>
        process.env.JEST_WORKER_ID
          ? handleNativeHttpRequestFetcher(
              buildApiEndpointUrl(apiName, path),
              'post',
              requestInit,
              undefined,
            )
          : AmplifyAPI.post(apiName, path, requestInit),
      ),
    );
  }

  static async put(
    apiName: string,
    path: string,
    init: initType,
  ): Promise<any> {
    const requestInit = await getInitForApi(apiName, init);
    return callApi(
      runWithSentrySpan('PUT', path, requestInit, () =>
        process.env.JEST_WORKER_ID
          ? handleNativeHttpRequestFetcher(
              buildApiEndpointUrl(apiName, path),
              'put',
              requestInit,
              undefined,
            )
          : AmplifyAPI.put(apiName, path, requestInit),
      ),
    );
  }

  static async patch(
    apiName: string,
    path: string,
    init: initType,
  ): Promise<any> {
    const requestInit = await getInitForApi(apiName, init);
    return callApi(
      runWithSentrySpan('PATCH', path, requestInit, () =>
        process.env.JEST_WORKER_ID
          ? handleNativeHttpRequestFetcher(
              buildApiEndpointUrl(apiName, path),
              'patch',
              requestInit,
              undefined,
            )
          : AmplifyAPI.patch(apiName, path, requestInit),
      ),
    );
  }

  static async del(
    apiName: string,
    path: string,
    init: initType,
  ): Promise<any> {
    const requestInit = await getInitForApi(apiName, init);

    return callApi(
      runWithSentrySpan('DELETE', path, requestInit, () =>
        process.env.JEST_WORKER_ID
          ? handleNativeHttpRequestFetcher(
              buildApiEndpointUrl(apiName, path),
              'del',
              requestInit,
              undefined,
            )
          : AmplifyAPI.del(apiName, path, requestInit),
      ),
    );
  }
}
